import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * A real modal, replacing the `window.prompt` the first pass used.
 *
 * `prompt` was functional but wrong for this: it cannot validate before closing,
 * cannot show why a reason is required, and cannot be styled — which matters when
 * the text being typed is emailed verbatim to an applicant.
 *
 * Handles the things a hand-rolled dialog usually forgets: Escape closes it,
 * focus moves into it on open and back out on close, Tab is trapped inside, the
 * scrim is click-to-dismiss, and the page behind does not scroll.
 */
export function Modal({
  title,
  children,
  onClose,
  footer,
  wide = false,
}: {
  title: string
  children: ReactNode
  onClose: () => void
  footer?: ReactNode
  wide?: boolean
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null

    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])',
    )
    focusable?.[0]?.focus()

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== 'Tab') return

      const items = panelRef.current?.querySelectorAll<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [href]',
      )
      if (!items || items.length === 0) return

      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = originalOverflow
      previouslyFocused.current?.focus()
    }
  }, [onClose])

  return (
    <div
      className="scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        style={wide ? { maxWidth: 620 } : undefined}
      >
        <div className="row" style={{ marginBottom: 'var(--s-5)' }}>
          <h2>{title}</h2>
          <div className="spacer" />
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>
        {children}
        {footer && (
          <div className="row" style={{ marginTop: 'var(--s-6)', gap: 'var(--s-2)' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Collects the reason a reject or flag requires.
 *
 * The server refuses an empty reason, and for a rejection it emails whatever is
 * typed to the applicant. So this validates before closing and says out loud that
 * the text will be sent — a reviewer should know that before they write "no".
 */
export function ReasonModal({
  kind,
  memberName,
  onCancel,
  onConfirm,
  busy = false,
}: {
  kind: 'reject' | 'flag'
  memberName: string
  onCancel: () => void
  onConfirm: (reason: string) => void
  busy?: boolean
}) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const isReject = kind === 'reject'

  function confirm(): void {
    const trimmed = reason.trim()
    if (trimmed.length < 10) {
      setError(
        isReject
          ? 'Please give a reason of at least 10 characters — this is emailed to the applicant.'
          : 'Please describe what needs checking, in at least 10 characters.',
      )
      return
    }
    onConfirm(trimmed)
  }

  return (
    <Modal
      title={isReject ? `Reject ${memberName}` : `Flag ${memberName}`}
      onClose={onCancel}
      footer={
        <>
          <div className="spacer" />
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className={isReject ? 'btn btn-danger' : 'btn btn-secondary'}
            onClick={confirm}
            disabled={busy}
          >
            {busy ? 'Saving…' : isReject ? 'Reject and email' : 'Flag for checking'}
          </button>
        </>
      }
    >
      <p className="small" style={{ marginTop: 0 }}>
        {isReject
          ? 'This reason is emailed to the applicant, so write it as something they can act on — which detail did not match the CCP file record?'
          : 'Flagging keeps the record out of the directory and marks it for a manual check against the file record. The note is for your colleagues, not the applicant.'}
      </p>

      <label className="kpi-label" htmlFor="reason">
        {isReject ? 'Reason for rejection' : 'What needs checking'}
      </label>
      <textarea
        id="reason"
        className="input"
        style={{ height: 110, padding: 'var(--s-3) var(--s-4)', lineHeight: 1.6 }}
        value={reason}
        onChange={(e) => {
          setReason(e.target.value)
          if (error) setError(null)
        }}
        placeholder={
          isReject
            ? 'e.g. The passing year does not match the file record for KitNo 8099.'
            : 'e.g. Same name and passing year as KitNo 3821 — needs a file check.'
        }
      />

      {error && (
        <div className="error-box" role="alert" style={{ marginTop: 'var(--s-3)' }}>
          {error}
        </div>
      )}
    </Modal>
  )
}

/** Simple confirm, for actions that need a deliberate second click. */
export function ConfirmModal({
  title,
  body,
  confirmLabel,
  danger = false,
  busy = false,
  onCancel,
  onConfirm,
}: {
  title: string
  body: string
  confirmLabel: string
  danger?: boolean
  busy?: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <div className="spacer" />
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className={danger ? 'btn btn-danger' : 'btn btn-accent'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="small" style={{ margin: 0 }}>
        {body}
      </p>
    </Modal>
  )
}

/**
 * Pagination. Every admin RPC pages server-side and returns the full `total` on
 * each row, so this only needs the numbers — and it renders nothing at all when
 * there is only one page, rather than showing dead controls.
 */
export function Pager({
  page,
  perPage,
  total,
  onChange,
  busy = false,
}: {
  page: number
  perPage: number
  total: number
  onChange: (page: number) => void
  busy?: boolean
}) {
  const pages = Math.max(1, Math.ceil(total / perPage))
  if (pages <= 1) return null

  const from = (page - 1) * perPage + 1
  const to = Math.min(page * perPage, total)

  return (
    <nav
      className="row"
      style={{ marginTop: 'var(--s-5)' }}
      aria-label="Pagination"
    >
      <span className="small muted">
        {from}–{to} of {total}
      </span>
      <div className="spacer" />
      <button
        className="btn btn-secondary btn-sm"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1 || busy}
      >
        Previous
      </button>
      <span className="small muted">
        Page {page} of {pages}
      </span>
      <button
        className="btn btn-secondary btn-sm"
        onClick={() => onChange(page + 1)}
        disabled={page >= pages || busy}
      >
        Next
      </button>
    </nav>
  )
}
